import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface ServerOfTheDay {
  id: string;
  name: string;
  slug: string | null;
  banner_image_url: string | null;
  logo_image_url: string | null;
  ip_address: string;
  monthly_votes: number;
  total_votes: number;
  online_players: number;
  max_players: number;
  is_online: boolean;
  version: string | null;
  tags: string[];
  custom_color: string | null;
  is_sponsored: boolean;
  prestige_expires_at: string | null;
  game_id: string;
  game_name: string;
  game_logo_url: string | null;
  today_votes: number;
}

// Calcule la date "effective" : à partir de 23h55, on considère déjà le jour suivant
const getEffectiveToday = () => {
  const now = new Date();
  // Si on est à 23h55 ou plus, on avance au jour suivant
  if (now.getHours() === 23 && now.getMinutes() >= 55) {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }
  return now.toISOString().split('T')[0];
};

// Calcule le temps restant avant le prochain changement (23h55)
const getMsUntilNextRefresh = () => {
  const now = new Date();
  const next2355 = new Date(now);
  next2355.setHours(23, 55, 0, 0);
  if (now >= next2355) {
    next2355.setDate(next2355.getDate() + 1);
  }
  return next2355.getTime() - now.getTime();
};

export const useServerOfTheDay = (gameId?: string) => {
  const effectiveToday = getEffectiveToday();

  return useQuery({
    queryKey: ['server-of-the-day', gameId || 'global', effectiveToday],
    queryFn: async (): Promise<ServerOfTheDay | null> => {
      const today = new Date().toISOString().split('T')[0];

      if (gameId) {
        const { data, error } = await supabase
          .from('daily_vote_stats')
          .select(`
            server_id,
            vote_count,
            servers!inner (
              id, name, slug, banner_image_url, logo_image_url,
              ip_address, monthly_votes, total_votes, online_players,
              max_players, is_online, version, tags, custom_color,
              is_sponsored, prestige_expires_at, game_id, is_approved, is_hidden,
              games!inner ( name, logo_url )
            )
          `)
          .eq('vote_date', today)
          .eq('servers.is_approved', true)
          .eq('servers.is_hidden', false)
          .eq('servers.game_id', gameId)
          .gt('vote_count', 0)
          .order('vote_count', { ascending: false })
          .limit(1);

        if (error || !data || data.length === 0) return null;
        return mapRow(data[0]);
      }

      const { data, error } = await supabase
        .from('daily_vote_stats')
        .select(`
          server_id,
          vote_count,
          servers!inner (
            id, name, slug, banner_image_url, logo_image_url,
            ip_address, monthly_votes, total_votes, online_players,
            max_players, is_online, version, tags, custom_color,
            is_sponsored, prestige_expires_at, game_id, is_approved, is_hidden,
            games!inner ( name, logo_url )
          )
        `)
        .eq('vote_date', today)
        .eq('servers.is_approved', true)
        .eq('servers.is_hidden', false)
        .gt('vote_count', 0)
        .order('vote_count', { ascending: false });

      if (error || !data || data.length === 0) return null;

      const topPerGame = new Map<string, any>();
      for (const row of data) {
        const gid = (row as any).servers.game_id;
        if (!topPerGame.has(gid)) {
          topPerGame.set(gid, row);
        }
      }

      const candidates = Array.from(topPerGame.values());
      const seed = effectiveToday.split('-').reduce((acc, n) => acc + parseInt(n, 10), 0);
      const index = seed % candidates.length;
      return mapRow(candidates[index]);
    },
    staleTime: getMsUntilNextRefresh(),
    refetchInterval: 5 * 60 * 1000, // Re-vérifie toutes les 5 minutes
  });

};

const mapRow = (row: any): ServerOfTheDay => {
  const server = row.servers;
  const game = server.games;
  return {
    id: server.id,
    name: server.name,
    slug: server.slug,
    banner_image_url: server.banner_image_url,
    logo_image_url: server.logo_image_url,
    ip_address: server.ip_address,
    monthly_votes: server.monthly_votes || 0,
    total_votes: server.total_votes || 0,
    online_players: server.online_players || 0,
    max_players: server.max_players || 0,
    is_online: server.is_online ?? true,
    version: server.version,
    tags: server.tags || [],
    custom_color: server.custom_color,
    is_sponsored: server.is_sponsored || false,
    prestige_expires_at: server.prestige_expires_at,
    game_id: server.game_id,
    game_name: game.name,
    game_logo_url: game.logo_url,
    today_votes: row.vote_count,
  };
};
